import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Input } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/input";
import { Label } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/radio-group";
import { Activity, ChevronLeft, X, Plus, Trash2 } from "lucide-react";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/tabs";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [showAddMedication, setShowAddMedication] = useState(false);
  const [newMedication, setNewMedication] = useState({
    name: "",
    doses: [{ time: "", frequency: "" }],
  });
  const [formData, setFormData] = useState({
    gender: "",
    race: "",
    dateOfBirth: "",
    heightFt: "",
    heightIn: "",
    heightCm: "",
    weightLbs: "",
    weightKg: "",
    diabetesType: "",
    medications: [] as any[],
    weightLossGoal: "",
    weightLossReason: "",
    targetWeight: "",
    targetWeightUnit: "lbs", // lbs or kg
    activityLevel: "",
    dietType: "",
    mealTimes: [] as string[],
    intermittentFasting: "",
    fastingWindow: "",
    heightUnit: "ft", // ft or cm
    weightUnit: "lbs", // lbs or kg
  });

  const totalSteps = 13;

  const handleNext = () => {
    if (step < totalSteps) {
      // Skip target weight screen (step 8) if weightLossGoal is not "Yes"
      if (step === 7 && formData.weightLossGoal !== "Yes") {
        setStep(9); // Jump to Activity Level
      } else {
        setStep(step + 1);
      }
    } else {
      // Save to localStorage and navigate to Fitbit connect
      localStorage.setItem("userProfile", JSON.stringify(formData));
      localStorage.setItem("onboardingComplete", "true");

      // Convert and save medications in the format expected by Home page
      if (formData.medications.length > 0) {
        const medicationsForHome = formData.medications.map(
          (med: any, index: number) => ({
            id: Date.now().toString() + index,
            name: med.name,
            doses: med.doses || [
              {
                time: "09:00",
                frequency: "Once daily",
              },
            ],
            active: true,
          })
        );
        localStorage.setItem("medications", JSON.stringify(medicationsForHome));
      }

      navigate("/fitbit-connect");
    }
  };

  const handleBack = () => {
    if (step > 1) {
      // Skip target weight screen when going back if weightLossGoal is not "Yes"
      if (step === 9 && formData.weightLossGoal !== "Yes") {
        setStep(7); // Go back to Weight Loss Goal
      } else {
        setStep(step - 1);
      }
    }
  };

  const handleSkip = () => {
    if (step === 8) {
      setStep(9); // Skip target weight to Activity Level
    }
  };

  const handleAddMedication = () => {
    if (
      newMedication.name.trim() &&
      newMedication.doses.every(d => d.time && d.frequency)
    ) {
      setFormData({
        ...formData,
        medications: [...formData.medications, { ...newMedication }],
      });
      setNewMedication({ name: "", doses: [{ time: "", frequency: "" }] });
      setShowAddMedication(false);
    }
  };

  const handleAddDose = () => {
    setNewMedication({
      ...newMedication,
      doses: [...newMedication.doses, { time: "", frequency: "" }],
    });
  };

  const handleRemoveDose = (index: number) => {
    const newDoses = newMedication.doses.filter((_, i) => i !== index);
    setNewMedication({ ...newMedication, doses: newDoses });
  };

  const handleDoseChange = (
    index: number,
    field: "time" | "frequency",
    value: string
  ) => {
    const newDoses = [...newMedication.doses];
    newDoses[index][field] = value;
    setNewMedication({ ...newMedication, doses: newDoses });
  };

  const handleRemoveMedication = (index: number) => {
    const updatedMedications = formData.medications.filter(
      (_, idx) => idx !== index
    );
    setFormData({ ...formData, medications: updatedMedications });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleAddMealTime = () => {
    setFormData({ ...formData, mealTimes: [...formData.mealTimes, ""] });
  };

  return (
    <div className="bg-gradient-hero flex min-h-screen flex-col">
      {/* Progress Bar */}
      <div className="h-1 w-full bg-muted">
        <div
          className="bg-gradient-primary h-full transition-all duration-300"
          style={{ width: `${(step / totalSteps) * 100}%` }}
        />
      </div>

      <div className="mx-auto flex w-full max-w-md flex-1 flex-col p-6">
        {/* Header */}
        <div className="mb-8 mt-4 flex items-center justify-between">
          {step > 1 && (
            <button
              onClick={handleBack}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border/50 bg-card/50 backdrop-blur-sm"
            >
              <ChevronLeft className="h-5 w-5 text-foreground" />
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <div className="bg-gradient-primary flex h-10 w-10 items-center justify-center rounded-2xl">
              <Activity className="h-6 w-6 text-primary-foreground" />
            </div>
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-xl font-bold text-transparent">
              Bite Balance
            </span>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="mb-6 text-center">
          <p className="text-sm text-muted-foreground">
            Assessment {step} of {totalSteps}
          </p>
        </div>

        {/* Step Content */}
        <div className="flex flex-1 flex-col justify-center">
          {/* Step 1: Gender */}
          {step === 1 && (
            <div className="space-y-6 duration-500 animate-in fade-in slide-in-from-bottom-4">
              <div>
                <h2 className="mb-2 text-3xl font-bold text-foreground">
                  What Is Your Gender?
                </h2>
                <p className="text-muted-foreground">
                  Gender affects diabetes management
                </p>
              </div>

              <RadioGroup
                value={formData.gender}
                onValueChange={value => handleInputChange("gender", value)}
              >
                {["Male", "Female", "Other"].map(option => (
                  <div
                    key={option}
                    className="flex items-center space-x-3 rounded-2xl border border-border/50 bg-card/50 p-4 backdrop-blur-sm"
                  >
                    <RadioGroupItem value={option} id={option} />
                    <Label
                      htmlFor={option}
                      className="flex-1 cursor-pointer text-foreground"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* Step 2: Race */}
          {step === 2 && (
            <div className="space-y-6 duration-500 animate-in fade-in slide-in-from-bottom-4">
              <div>
                <h2 className="mb-2 text-3xl font-bold text-foreground">
                  What Is Your Race?
                </h2>
                <p className="text-muted-foreground">
                  Race helps in identifying appropriate treatment as diabetes
                  management can vary by your race
                </p>
              </div>

              <RadioGroup
                value={formData.race}
                onValueChange={value => handleInputChange("race", value)}
              >
                {[
                  "White",
                  "Black or African American",
                  "Asian",
                  "American Indian or Alaska Native",
                  "Native Hawaiian or Other Pacific Islander",
                  "Middle Eastern or North African",
                ].map(option => (
                  <div
                    key={option}
                    className="flex items-center space-x-3 rounded-2xl border border-border/50 bg-card/50 p-4 backdrop-blur-sm"
                  >
                    <RadioGroupItem value={option} id={option} />
                    <Label
                      htmlFor={option}
                      className="flex-1 cursor-pointer text-foreground"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* Step 3: Date of Birth */}
          {step === 3 && (
            <div className="space-y-6 duration-500 animate-in fade-in slide-in-from-bottom-4">
              <div>
                <h2 className="mb-2 text-3xl font-bold text-foreground">
                  What Is Your Date Of Birth?
                </h2>
                <p className="text-muted-foreground">
                  Your date of birth helps us personalize your diabetes
                  management
                </p>
              </div>

              <div>
                <Input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={e =>
                    handleInputChange("dateOfBirth", e.target.value)
                  }
                  className="h-14 text-lg"
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
            </div>
          )}

          {/* Step 4: Height & Weight */}
          {step === 4 && (
            <div className="space-y-6 duration-500 animate-in fade-in slide-in-from-bottom-4">
              <div>
                <h2 className="mb-2 text-3xl font-bold text-foreground">
                  Height & Weight
                </h2>
                <p className="text-muted-foreground">
                  Select your preferred units and enter your measurements
                </p>
              </div>

              {/* Height */}
              <div className="space-y-3">
                <Label className="block text-base font-semibold text-foreground">
                  Height
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: "ft", label: "Ft - Inch", desc: "e.g. 5' 8\"" },
                    { value: "cm", label: "Centimeters", desc: "e.g. 173 cm" },
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() =>
                        handleInputChange("heightUnit", option.value)
                      }
                      className={`rounded-2xl border-2 p-3 text-center transition-all ${
                        formData.heightUnit === option.value
                          ? "scale-[1.02] border-primary bg-primary/10 shadow-lg"
                          : "border-border bg-card/50 hover:border-primary/50"
                      }`}
                    >
                      <span className="block text-sm font-bold text-foreground">
                        {option.label}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {option.desc}
                      </span>
                    </button>
                  ))}
                </div>
                {formData.heightUnit === "ft" ? (
                  <div className="grid grid-cols-2 gap-3 duration-300 animate-in fade-in">
                    <Input
                      type="number"
                      placeholder="Feet"
                      value={formData.heightFt}
                      onChange={e =>
                        handleInputChange("heightFt", e.target.value)
                      }
                      className="h-12 text-center text-lg"
                    />
                    <Input
                      type="number"
                      placeholder="Inches"
                      value={formData.heightIn}
                      onChange={e =>
                        handleInputChange("heightIn", e.target.value)
                      }
                      className="h-12 text-center text-lg"
                    />
                  </div>
                ) : (
                  <div className="duration-300 animate-in fade-in">
                    <Input
                      type="number"
                      placeholder="Height in cm"
                      value={formData.heightCm}
                      onChange={e =>
                        handleInputChange("heightCm", e.target.value)
                      }
                      className="h-12 text-center text-lg"
                    />
                  </div>
                )}
              </div>

              {/* Weight */}
              <div className="space-y-3">
                <Label className="block text-base font-semibold text-foreground">
                  Weight
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: "kg", label: "Kilograms", desc: "e.g. 70 kg" },
                    { value: "lbs", label: "Pounds", desc: "e.g. 154 lbs" },
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() =>
                        handleInputChange("weightUnit", option.value)
                      }
                      className={`rounded-2xl border-2 p-3 text-center transition-all ${
                        formData.weightUnit === option.value
                          ? "scale-[1.02] border-primary bg-primary/10 shadow-lg"
                          : "border-border bg-card/50 hover:border-primary/50"
                      }`}
                    >
                      <span className="block text-sm font-bold text-foreground">
                        {option.label}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {option.desc}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="duration-300 animate-in fade-in">
                  <Input
                    type="number"
                    placeholder={
                      formData.weightUnit === "lbs"
                        ? "Weight in lbs"
                        : "Weight in kg"
                    }
                    value={
                      formData.weightUnit === "lbs"
                        ? formData.weightLbs
                        : formData.weightKg
                    }
                    onChange={e =>
                      handleInputChange(
                        formData.weightUnit === "lbs"
                          ? "weightLbs"
                          : "weightKg",
                        e.target.value
                      )
                    }
                    className="h-12 text-center text-lg"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Diabetes Type */}
          {step === 5 && (
            <div className="space-y-6 duration-500 animate-in fade-in slide-in-from-bottom-4">
              <div>
                <h2 className="mb-2 text-3xl font-bold text-foreground">
                  Type Of Your Diabetes
                </h2>
                <p className="text-muted-foreground">
                  Select your type of diabetes
                </p>
              </div>

              <RadioGroup
                value={formData.diabetesType}
                onValueChange={value =>
                  handleInputChange("diabetesType", value)
                }
              >
                {[
                  {
                    value: "Type 1",
                    info: "Typically genetic or hereditary in nature. The body's immune system attacks insulin-producing cells.",
                  },
                  {
                    value: "Type 2",
                    info: "More influenced by lifestyle factors such as diet, physical inactivity, and obesity.",
                  },
                  {
                    value: "Prediabetic",
                    info: "Higher-than-normal blood sugar levels, but not high enough for Type 2 diabetes.",
                  },
                  { value: "None", info: "No diabetes diagnosis" },
                ].map(option => (
                  <div
                    key={option.value}
                    className="flex items-start space-x-3 rounded-2xl border border-border/50 bg-card/50 p-4 backdrop-blur-sm"
                  >
                    <RadioGroupItem
                      value={option.value}
                      id={option.value}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor={option.value}
                        className="cursor-pointer font-semibold text-foreground"
                      >
                        {option.value}
                      </Label>
                      {formData.diabetesType === option.value && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {option.info}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* Step 6: Medications */}
          {step === 6 && (
            <div className="space-y-6 duration-500 animate-in fade-in slide-in-from-bottom-4">
              <div>
                <h2 className="mb-2 text-3xl font-bold text-foreground">
                  Your Current Medications
                </h2>
                <p className="text-muted-foreground">
                  Add your current medications (optional)
                </p>
              </div>

              <div className="space-y-3">
                {formData.medications.map((med, idx) => (
                  <Card
                    key={idx}
                    className="relative rounded-2xl border border-border/50 bg-card/50 p-4 backdrop-blur-sm"
                  >
                    <button
                      onClick={() => handleRemoveMedication(idx)}
                      className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-destructive/10 transition-colors hover:bg-destructive/20"
                    >
                      <X className="h-4 w-4 text-destructive" />
                    </button>
                    <div className="pr-8">
                      <p className="font-semibold text-foreground">
                        {med.name}
                      </p>
                      <div className="mt-1 space-y-1">
                        {med.doses?.map((dose: any, idx: number) => (
                          <p
                            key={idx}
                            className="text-sm text-muted-foreground"
                          >
                            {dose.frequency} • {dose.time}
                          </p>
                        ))}
                      </div>
                    </div>
                  </Card>
                ))}

                {!showAddMedication ? (
                  <Button
                    variant="outline"
                    onClick={() => setShowAddMedication(true)}
                    className="h-12 w-full rounded-2xl"
                  >
                    Add Medication
                  </Button>
                ) : (
                  <Card className="rounded-2xl border-2 border-primary/50 bg-card/50 p-4 backdrop-blur-sm">
                    <div className="space-y-3">
                      <div>
                        <Label className="mb-2 block text-foreground">
                          Medication Name *
                        </Label>
                        <Input
                          placeholder="e.g., Metformin"
                          value={newMedication.name}
                          onChange={e =>
                            setNewMedication({
                              ...newMedication,
                              name: e.target.value,
                            })
                          }
                          className="h-12"
                        />
                      </div>

                      <div>
                        <div className="mb-2 flex items-center justify-between">
                          <Label className="text-foreground">
                            Daily Doses *
                          </Label>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={handleAddDose}
                            className="h-8 text-xs"
                          >
                            Add Dose
                          </Button>
                        </div>

                        <div className="space-y-2">
                          {newMedication.doses.map((dose, index) => (
                            <div
                              key={index}
                              className="space-y-2 rounded-xl border border-border/50 bg-card p-3"
                            >
                              <div className="mb-1 flex items-center justify-between">
                                <Label className="text-xs">
                                  Dose {index + 1}
                                </Label>
                                {newMedication.doses.length > 1 && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleRemoveDose(index)}
                                    className="h-6 w-6 p-0 text-destructive"
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>

                              <div>
                                <Label className="text-xs">Frequency</Label>
                                <Input
                                  value={dose.frequency}
                                  onChange={e =>
                                    handleDoseChange(
                                      index,
                                      "frequency",
                                      e.target.value
                                    )
                                  }
                                  placeholder="e.g., With breakfast"
                                  className="mt-1 h-10"
                                />
                              </div>

                              <div>
                                <Label className="text-xs">Time</Label>
                                <Input
                                  type="time"
                                  value={dose.time}
                                  onChange={e =>
                                    handleDoseChange(
                                      index,
                                      "time",
                                      e.target.value
                                    )
                                  }
                                  className="mt-1 h-10"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={handleAddMedication}
                          disabled={
                            !newMedication.name.trim() ||
                            !newMedication.doses.every(
                              d => d.time && d.frequency
                            )
                          }
                          className="h-11 flex-1 rounded-xl"
                        >
                          Add
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowAddMedication(false);
                            setNewMedication({
                              name: "",
                              doses: [{ time: "", frequency: "" }],
                            });
                          }}
                          className="h-11 flex-1 rounded-xl"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Step 7: Weight Loss Goal */}
          {step === 7 && (
            <div className="space-y-6 duration-500 animate-in fade-in slide-in-from-bottom-4">
              <div>
                <h2 className="mb-2 text-3xl font-bold text-foreground">
                  Weight Loss Goal
                </h2>
                <p className="text-muted-foreground">
                  Are you currently trying to lose weight as part of your health
                  goals?
                </p>
              </div>

              <RadioGroup
                value={formData.weightLossGoal}
                onValueChange={value =>
                  handleInputChange("weightLossGoal", value)
                }
              >
                {["Yes", "No", "Not Sure"].map(option => (
                  <div
                    key={option}
                    className="flex items-center space-x-3 rounded-2xl border border-border/50 bg-card/50 p-4 backdrop-blur-sm"
                  >
                    <RadioGroupItem value={option} id={option} />
                    <Label
                      htmlFor={option}
                      className="flex-1 cursor-pointer text-foreground"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              {formData.weightLossGoal === "Yes" && (
                <div className="space-y-3 pt-4">
                  <Label className="text-foreground">
                    What's your main reason?
                  </Label>
                  <RadioGroup
                    value={formData.weightLossReason}
                    onValueChange={value =>
                      handleInputChange("weightLossReason", value)
                    }
                  >
                    {[
                      "To improve blood sugar / manage diabetes",
                      "Doctor recommended it",
                      "General health / feel better",
                      "To look or feel more confident",
                      "Other",
                    ].map(option => (
                      <div
                        key={option}
                        className="flex items-center space-x-3 rounded-xl border border-border/30 bg-card/30 p-3"
                      >
                        <RadioGroupItem
                          value={option}
                          id={`reason-${option}`}
                        />
                        <Label
                          htmlFor={`reason-${option}`}
                          className="flex-1 cursor-pointer text-sm text-foreground"
                        >
                          {option}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}
            </div>
          )}

          {/* Step 8: Target Weight */}
          {step === 8 && formData.weightLossGoal === "Yes" && (
            <div className="space-y-6 duration-500 animate-in fade-in slide-in-from-bottom-4">
              <div>
                <h2 className="mb-2 text-3xl font-bold text-foreground">
                  What's Your Target Weight?
                </h2>
                <p className="text-muted-foreground">
                  Set your weight goal (optional)
                </p>
              </div>

              <div className="space-y-4">
                <Tabs
                  value={formData.targetWeightUnit}
                  onValueChange={value =>
                    handleInputChange("targetWeightUnit", value)
                  }
                  className="mb-3"
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="lbs">Lbs</TabsTrigger>
                    <TabsTrigger value="kg">Kg</TabsTrigger>
                  </TabsList>
                </Tabs>

                <Input
                  type="number"
                  placeholder={
                    formData.targetWeightUnit === "lbs"
                      ? "Target weight in Lbs"
                      : "Target weight in Kg"
                  }
                  value={formData.targetWeight}
                  onChange={e =>
                    handleInputChange("targetWeight", e.target.value)
                  }
                  className="h-14 text-center text-lg"
                />
              </div>
            </div>
          )}

          {/* Step 9: Activity Level */}
          {step === 9 && (
            <div className="space-y-6 duration-500 animate-in fade-in slide-in-from-bottom-4">
              <div>
                <h2 className="mb-2 text-3xl font-bold text-foreground">
                  How Physically Active Are You?
                </h2>
                <p className="text-muted-foreground">
                  This helps us understand how your activity impacts blood sugar
                </p>
              </div>

              <RadioGroup
                value={formData.activityLevel}
                onValueChange={value =>
                  handleInputChange("activityLevel", value)
                }
              >
                {[
                  { value: "Sedentary", desc: "Mostly sitting" },
                  {
                    value: "Lightly active",
                    desc: "3-5 walks or light workouts/week",
                  },
                  {
                    value: "Moderately active",
                    desc: "Exercise 3-5x/week, mix of cardio & strength",
                  },
                  {
                    value: "Very active",
                    desc: "6+ high-intensity sessions/week",
                  },
                ].map(option => (
                  <div
                    key={option.value}
                    className="flex items-start space-x-3 rounded-2xl border border-border/50 bg-card/50 p-4 backdrop-blur-sm"
                  >
                    <RadioGroupItem
                      value={option.value}
                      id={option.value}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor={option.value}
                        className="cursor-pointer font-semibold text-foreground"
                      >
                        {option.value}
                      </Label>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {option.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* Step 10: Diet Type */}
          {step === 10 && (
            <div className="space-y-6 duration-500 animate-in fade-in slide-in-from-bottom-4">
              <div>
                <h2 className="mb-2 text-3xl font-bold text-foreground">
                  Do You Follow A Specific Diet?
                </h2>
                <p className="text-muted-foreground">
                  Select your diet preference
                </p>
              </div>

              <RadioGroup
                value={formData.dietType}
                onValueChange={value => handleInputChange("dietType", value)}
              >
                {[
                  "Classic",
                  "Vegetarian",
                  "Vegan",
                  "Pescatarian",
                  "Low Carb / Keto",
                ].map(option => (
                  <div
                    key={option}
                    className="flex items-center space-x-3 rounded-2xl border border-border/50 bg-card/50 p-4 backdrop-blur-sm"
                  >
                    <RadioGroupItem value={option} id={option} />
                    <Label
                      htmlFor={option}
                      className="flex-1 cursor-pointer text-foreground"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* Step 11: Meal Times */}
          {step === 11 && (
            <div className="space-y-6 duration-500 animate-in fade-in slide-in-from-bottom-4">
              <div>
                <h2 className="mb-2 text-3xl font-bold text-foreground">
                  When Do You Usually Eat Your Meals?
                </h2>
                <p className="text-muted-foreground">
                  Setting your typical meal times helps better predict balance
                  levels
                </p>
              </div>

              <div className="space-y-3">
                {["First Meal", "Second Meal", "Third Meal"].map(
                  (meal, idx) => (
                    <div key={idx}>
                      <Label className="mb-2 block text-foreground">
                        {meal}
                      </Label>
                      <Input
                        type="time"
                        className="h-12"
                        value={formData.mealTimes[idx] || ""}
                        onChange={e => {
                          const newMealTimes = [...formData.mealTimes];
                          newMealTimes[idx] = e.target.value;
                          setFormData({ ...formData, mealTimes: newMealTimes });
                        }}
                      />
                    </div>
                  )
                )}
                <p className="pt-2 text-center text-xs text-muted-foreground">
                  Skip if your time is not fixed
                </p>
              </div>
            </div>
          )}

          {/* Step 12: Intermittent Fasting */}
          {step === 12 && (
            <div className="space-y-6 duration-500 animate-in fade-in slide-in-from-bottom-4">
              <div>
                <h2 className="mb-2 text-3xl font-bold text-foreground">
                  Are You Currently Following Intermittent Fasting?
                </h2>
              </div>

              <RadioGroup
                value={formData.intermittentFasting}
                onValueChange={value =>
                  handleInputChange("intermittentFasting", value)
                }
              >
                {[
                  "Yes — I follow a fasting window",
                  "Sometimes / Not sure",
                  "No — I eat throughout the day",
                ].map(option => (
                  <div
                    key={option}
                    className="flex items-center space-x-3 rounded-2xl border border-border/50 bg-card/50 p-4 backdrop-blur-sm"
                  >
                    <RadioGroupItem value={option} id={option} />
                    <Label
                      htmlFor={option}
                      className="flex-1 cursor-pointer text-foreground"
                    >
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              {formData.intermittentFasting ===
                "Yes — I follow a fasting window" && (
                <div className="space-y-3 pt-4">
                  <Label className="text-foreground">
                    What's your typical eating window?
                  </Label>
                  <RadioGroup
                    value={formData.fastingWindow}
                    onValueChange={value =>
                      handleInputChange("fastingWindow", value)
                    }
                  >
                    {[
                      "16:8 (Fast 16 hrs / Eat 8 hrs)",
                      "18:6",
                      "14:10",
                      "My eating window varies",
                    ].map(option => (
                      <div
                        key={option}
                        className="flex items-center space-x-3 rounded-xl border border-border/30 bg-card/30 p-3"
                      >
                        <RadioGroupItem
                          value={option}
                          id={`window-${option}`}
                        />
                        <Label
                          htmlFor={`window-${option}`}
                          className="flex-1 cursor-pointer text-sm text-foreground"
                        >
                          {option}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}
            </div>
          )}

          {/* Step 13: Thank You */}
          {step === 13 && (
            <div className="space-y-6 text-center duration-500 animate-in fade-in slide-in-from-bottom-4">
              <div className="bg-gradient-primary/10 mx-auto flex h-20 w-20 items-center justify-center rounded-full">
                <Activity className="h-10 w-10 text-primary" />
              </div>
              <div>
                <h2 className="mb-3 text-4xl font-bold text-foreground">
                  Thank You
                </h2>
                <p className="mb-2 text-xl text-foreground">For Trusting Us</p>
                <p className="text-muted-foreground">
                  We promise to always keep your personal information private
                  and secure.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Next Button */}
        <div className="pt-6">
          {step === 8 && formData.weightLossGoal === "Yes" ? (
            <div className="space-y-3">
              <Button
                onClick={handleNext}
                className="bg-gradient-primary h-14 w-full rounded-2xl text-lg shadow-primary hover:opacity-90"
              >
                Next
              </Button>
              <Button
                onClick={handleSkip}
                variant="ghost"
                className="h-12 w-full rounded-2xl text-base"
              >
                Skip
              </Button>
            </div>
          ) : (
            <Button
              onClick={handleNext}
              className="bg-gradient-primary h-14 w-full rounded-2xl text-lg shadow-primary hover:opacity-90"
            >
              {step === totalSteps ? "Create My Plan" : "Next"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
